'use client'

import { Navigate, useParams } from '@/lib/router'
import { SiteShell } from '@/components/site/site-shell'
import { PageIntro } from '@/components/site/page-intro'
import { PageCrumbs } from '@/components/site/page-crumbs'
import { FaqList } from '@/components/site/faq-list'
import { MagneticLink } from '@/components/site/magnetic-button'
import { getServiceBySlug } from '@/content/services'
import { BreadcrumbJsonLd } from '@/seo/BreadcrumbJsonLd'
import { FaqJsonLd } from '@/seo/FaqJsonLd'

export default function ServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const page = getServiceBySlug(slug)

  if (!page) {
    return <Navigate to="/services" replace />
  }

  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: page.eyebrow, path: page.path },
  ]

  return (
    <SiteShell>
      <BreadcrumbJsonLd items={crumbs} />
      <FaqJsonLd faqs={page.faqs} />
      <div className="w-full bg-background px-6 pb-20 pt-24 md:pb-32 lg:px-12">
        <div className="mx-auto max-w-3xl">
          <PageCrumbs items={crumbs} />
          <PageIntro
            eyebrow={page.eyebrow}
            title={
              <>
                {page.headline}
                <br />
                <span className="italic text-muted-foreground">{page.headlineAccent}</span>
              </>
            }
            description={page.lede}
          />

          <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
            {page.intro.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>

          <ul className="mt-16 space-y-10">
            {page.offerings.map((item) => (
              <li key={item.title}>
                <h2 className="mb-2 font-serif text-2xl text-foreground">{item.title}</h2>
                <p className="leading-relaxed text-muted-foreground">{item.body}</p>
              </li>
            ))}
          </ul>

          <FaqList faqs={page.faqs} />

          <div className="mt-20 border-t border-border pt-16">
            <p className="mb-8 font-serif text-2xl italic text-muted-foreground">
              Ready to start a project?
            </p>
            <MagneticLink href="/contact" variant="primary">
              Get in touch
            </MagneticLink>
          </div>
        </div>
      </div>
    </SiteShell>
  )
}
